// Display-layer formatters used across the app.
//
// Phone, date/time, label, and entity-label helpers consolidated here. Heavy
// callers were `events/`, `members/`, and `matches/`; behavior is preserved
// verbatim from the legacy bundle.

import { h } from "./dom";

// ---------- Phone ----------

export function formatKoPhone(digits: string | number | null | undefined): string {
  const d = String(digits || "").replace(/\D/g, "").slice(0, 11);
  if (!d) return "";
  if (d.startsWith("02")) {
    if (d.length <= 2) return d;
    if (d.length <= 5) return `${d.slice(0, 2)}-${d.slice(2)}`;
    if (d.length <= 9) return `${d.slice(0, 2)}-${d.slice(2, 5)}-${d.slice(5)}`;
    return `${d.slice(0, 2)}-${d.slice(2, 6)}-${d.slice(6, 10)}`;
  }
  if (d.length <= 3) return d;
  if (d.length <= 7) return `${d.slice(0, 3)}-${d.slice(3)}`;
  if (d.length <= 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7, 11)}`;
}

// Returns formatted value if input looks phone-like (digits, dashes, spaces, +, parens),
// otherwise returns raw — so kakao IDs or names aren't mangled.
export function autoFormatPhoneInput(raw: string | null | undefined): string {
  const s = String(raw || "");
  if (!s) return "";
  if (/[^\d\s\-+()]/.test(s)) return s;
  return formatKoPhone(s);
}

export function attachPhoneFormatter(input: HTMLInputElement): HTMLInputElement {
  const reformat = () => {
    const before = input.value;
    const after = autoFormatPhoneInput(before);
    if (after !== before) {
      input.value = after;
      try {
        input.setSelectionRange(after.length, after.length);
      } catch {
        // setSelectionRange throws on some input types (e.g., type="number").
      }
    }
  };
  input.addEventListener("input", reformat);
  if (input.value) reformat();
  return input;
}

// ---------- Date / time ----------

export function kstTodayStr(): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const lookup = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return `${lookup.year}-${lookup.month}-${lookup.day}`;
}

export function formatDateKo(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
  if (isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`;
}

export function formatTimeShort(iso: string | null | undefined): string {
  if (!iso) return "";
  const m = String(iso).match(/T(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : "";
}

export function formatGuestExpiresAt(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const m = d.getMonth() + 1;
  const day = d.getDate();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${m}월 ${day}일 ${hh}:${mm}`;
}

export function shiftIsoByMinutes(iso: string | null | undefined, minutes: number): string {
  if (!iso) return iso ?? "";
  const m = String(iso).match(/^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2}):(\d{2})(Z|[+-]\d{2}:?\d{2})?$/);
  if (!m) return iso;
  const offsetStr = m[5] || "";
  const pad = (n: number) => String(n).padStart(2, "0");
  if (!offsetStr) {
    // Naive ISO (no timezone) — shift in UTC arithmetic without drift
    const base = new Date(
      Date.UTC(
        parseInt(m[1].slice(0, 4), 10),
        parseInt(m[1].slice(5, 7), 10) - 1,
        parseInt(m[1].slice(8, 10), 10),
        parseInt(m[2], 10),
        parseInt(m[3], 10),
        parseInt(m[4], 10),
      ),
    );
    const shifted = new Date(base.getTime() + minutes * 60000);
    const date = `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())}`;
    const time = `${pad(shifted.getUTCHours())}:${pad(shifted.getUTCMinutes())}:${pad(shifted.getUTCSeconds())}`;
    return `${date}T${time}`;
  }
  const d = new Date(iso);
  d.setMinutes(d.getMinutes() + minutes);
  let offsetMin = 0;
  if (offsetStr !== "Z") {
    const om = offsetStr.match(/^([+-])(\d{2}):?(\d{2})$/);
    if (om) offsetMin = (om[1] === "-" ? -1 : 1) * (parseInt(om[2], 10) * 60 + parseInt(om[3], 10));
  }
  const local = new Date(d.getTime() + offsetMin * 60000);
  const date = `${local.getUTCFullYear()}-${pad(local.getUTCMonth() + 1)}-${pad(local.getUTCDate())}`;
  const time = `${pad(local.getUTCHours())}:${pad(local.getUTCMinutes())}:${pad(local.getUTCSeconds())}`;
  return `${date}T${time}${offsetStr}`;
}

export function isoMinuteDiff(a: string | null | undefined, b: string | null | undefined): number {
  if (!a || !b) return 0;
  return Math.round((new Date(a).getTime() - new Date(b).getTime()) / 60000);
}

// Add minutes to a naive (no-timezone) local ISO string and return another
// naive ISO. Used for event scheduling, which works in the user's wall clock.
export function addMinutesToLocalISO(localISO: string, minutes: number): string {
  const d = new Date(localISO);
  d.setMinutes(d.getMinutes() + minutes);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

// Returns the YYYY-MM-DD date string for the next occurrence of the given
// day-of-week (0=Sun..6=Sat). If today matches, returns today (diff=0).
export function nextDateForDayOfWeek(
  dow: number | null | undefined,
  fromDate: Date = new Date(),
): string | Date {
  if (dow === null || dow === undefined) return fromDate;
  const base = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
  const diff = ((dow - base.getDay()) + 7) % 7;
  base.setDate(base.getDate() + (diff === 0 ? 0 : diff));
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${base.getFullYear()}-${pad(base.getMonth() + 1)}-${pad(base.getDate())}`;
}

export const DAY_LABELS = ["일", "월", "화", "수", "목", "금", "토"];

export interface DateParts {
  month: string;
  day: number | "";
  weekday: number | null;
  isToday: boolean;
}

export function parseDateParts(iso: string | null | undefined): DateParts {
  if (!iso) return { month: "", day: "", weekday: null, isToday: false };
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
  if (isNaN(d.getTime())) return { month: "", day: "", weekday: null, isToday: false };
  const months = [
    "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
    "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
  ];
  const now = new Date();
  const isToday =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  return {
    month: isToday ? "TODAY" : months[d.getMonth()],
    day: isToday ? "" : d.getDate(),
    weekday: d.getDay(),
    isToday,
  };
}

// 대한민국 공휴일 (대체공휴일 포함). 매년 1월에 수동 업데이트 필요.
const KR_HOLIDAYS = new Set([
  "2026-01-01", // 신정
  "2026-02-16", "2026-02-17", "2026-02-18", // 설날 연휴
  "2026-03-01", "2026-03-02", // 삼일절(일) + 대체
  "2026-05-05", // 어린이날
  "2026-05-24", "2026-05-25", // 부처님오신날(일) + 대체
  "2026-06-06", // 현충일
  "2026-08-15", "2026-08-17", // 광복절(토) + 대체
  "2026-09-24", "2026-09-25", "2026-09-26", "2026-09-28", // 추석 연휴 + 대체
  "2026-10-03", "2026-10-05", // 개천절(토) + 대체
  "2026-10-09", // 한글날
  "2026-12-25", // 성탄절
]);

export function isKoreanHoliday(iso: string | null | undefined): boolean {
  if (!iso) return false;
  return KR_HOLIDAYS.has(iso.slice(0, 10));
}

// ---------- Entity labels ----------

export function eventKindLabel(ev: any): string {
  if (ev?.event_visibility === "personal") return "내 모임";
  if (ev?.event_type === "regular") return "정기 모임";
  if (ev?.mode === "team") return ev?.is_friendly_match ? "교류전" : "팀전";
  return "개인전";
}

export function statusLabel(status: string): string {
  return (
    {
      draft: "준비 중",
      ready: "준비 완료",
      in_progress: "진행 중",
      completed: "종료",
      archived: "보관",
    } as Record<string, string>
  )[status] || status;
}

export function statusChipClass(status: string): string {
  if (status === "in_progress") return "chip chip--live";
  if (status === "draft") return "chip chip--draft";
  if (status === "ready") return "chip chip--ready";
  return "chip chip--done";
}

export function genderLabel(g: string | null | undefined): string {
  return g === "male" ? "남" : g === "female" ? "여" : g === "other" ? "기타" : "";
}

// 0→A, 1→B, ...
export function courtLetter(i: number): string {
  return String.fromCharCode(65 + i);
}

export const SURFACE_OPTIONS: Array<[string, string]> = [
  ["turf", "인잔"],
  ["hard", "하드"],
  ["clay", "클레이"],
];

export const SURFACE_LABELS: Record<string, string> = { turf: "인잔", hard: "하드", clay: "클레이" };

export const DOMINANT_HAND_LABELS: Record<string, string> = { right: "오른손", left: "왼손" };
export const BACKHAND_STYLE_LABELS: Record<string, string> = { two: "투핸드", one: "원핸드" };

export function surfaceLabel(s: string | null | undefined): string | null {
  if (!s) return null;
  return SURFACE_LABELS[s] || null;
}

export function buildSurfaceSelect(
  current: string | null | undefined,
  onChange: (v: string | null) => void,
): HTMLSelectElement {
  const sel = h("select", { class: "input input--compact surface-select" }) as HTMLSelectElement;
  const blank = h("option", { value: "" }, "표면") as HTMLOptionElement;
  sel.appendChild(blank);
  for (const [v, l] of SURFACE_OPTIONS) {
    const opt = h("option", { value: v }, l) as HTMLOptionElement;
    if (v === current) opt.selected = true;
    sel.appendChild(opt);
  }
  if (!current) blank.selected = true;
  sel.onchange = () => onChange(sel.value || null);
  return sel;
}

export function formatNtrp(v: number | string | null | undefined): string {
  if (v == null || v === "") return "";
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(1) : String(v);
}
